import { useCallback, useEffect, useMemo, useState } from "react";
import UnassociatedUnitsCard from "./UnassociatedUnitsCard";
import SearchComponent from "./Common/SearchComponent";
import { fetchAllUnits } from "../lib/api";
import { fetchAllPods } from "../utils/fleetUtils";
import { assignUnitToZone, clearsPendingZone, getUnitZone, isAwaitingConnection, PENDING_ZONE_ASSIGNED_MESSAGE, PENDING_ZONE_REMOVED_MESSAGE, removeUnitFromZone, withZoneAssigned, withZoneCleared } from "../utils/unitZoneUtils";
import LoadingModal from "./Common/LoadingModal";
import ConfirmModal from "./Common/ConfirmModal";
import ToastMessage from './Common/ToastMessage';
import UnassociatedUnitsList from "./UnassociatedUnitsList";
import GridListViewComponent from "./Common/GridListViewComponent";
import { useLocation, useNavigate } from "react-router-dom";
import ZoneModal from "./Common/ZoneModal";
import Pagination from "./Common/Pagination";
import BulkUnitSelectionContainer from "./BulkUnitSelectionContainer";
import FilterDropDown from "./Common/FilterDropDown";
import SortByDropdown from "./Common/SortByFilter";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import { SearchContext } from "../lib/analyticsEvents";
import {
    ALL_UNIT_FILTER_SECTIONS,
    ALL_UNIT_LIST_QUERY,
    ALL_UNIT_SORT_OPTIONS,
    FIRST_PAGE,
    ORDER_OPTIONS,
    emptyFilters,
    hasActiveFilters,
    lastPageForCount,
    readUnitListQuery,
    toAllUnitApiFilters,
    writeUnitListQuery,
} from "../utils/unitListQuery";

const DEFAULT_PAGE_SIZE = 25;
const GRID_VIEW_STORAGE_KEY = "unassociatedUnitsViewMode";

export default function UnassociatedUnits() {
    const isActionsDisabled = useIsWriteRestrictedUser();
    const getStoredGridView = () => {
        if (window.innerWidth < 1025) return true;
        try {
            const stored = localStorage.getItem(GRID_VIEW_STORAGE_KEY);
            return stored ? stored === "grid" : true;
        } catch (error) {
            return true;
        }
    };
    const [unassociatedUnits, setUnassociatedUnits] = useState([]);
    const [showDeleteModel, setShowDeleteModel] = useState(false);
    const [selectedUnitInfo, setSelectedUnitInfo] = useState(null);
    const [loading, setLoading] = useState(false)
    const [showZoneModal, setShowEditModal] = useState(false);
    const [gridView, setGridView] = useState(() => getStoredGridView());
    const [totalCount, setTotalCount] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();

    const [toast, setToast] = useState({
        show: false,
        message: '',
        type: 'success',
    });
    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
    }, [setToast]);

    useEffect(() => {
        if (location.state?.toast) {
            setToast({
                show: true,
                message: location.state.toast.message,
                type: location.state.toast.type,
            });
            navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, location.search, navigate]);

    const { page, search: searchUnitText, sortBy, sortDirection, filters } = useMemo(
        () => readUnitListQuery(location.search, ALL_UNIT_LIST_QUERY),
        [location.search]
    );
    const currentPage = page - FIRST_PAGE;

    const updateQuery = useCallback((changes, { replace = false } = {}) => {
        const current = readUnitListQuery(location.search, ALL_UNIT_LIST_QUERY);
        navigate({
            pathname: location.pathname,
            search: writeUnitListQuery(location.search, ALL_UNIT_LIST_QUERY, { ...current, ...changes }),
        }, { replace });
    }, [location.pathname, location.search, navigate]);

    const apiFilters = useMemo(() => toAllUnitApiFilters(filters), [filters]);

    const fetchUnits = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAllUnits({
                pageNumber: page,
                pageSize: DEFAULT_PAGE_SIZE,
                search: searchUnitText,
                sortBy,
                sortDirection,
                ...apiFilters,
            });
            const units = res?.data?.units || [];
            const count = res?.data?.count || 0;

            setUnassociatedUnits(units); // always replace
            setTotalCount(count);

            const lastPage = lastPageForCount(count, DEFAULT_PAGE_SIZE);
            if (page > lastPage) {
                updateQuery({ page: lastPage }, { replace: true });
            }
        } catch (err) {
            console.error("Could not load units", err);
        } finally {
            setLoading(false);
        }
    }, [apiFilters, searchUnitText, sortBy, sortDirection, page, updateQuery]);

    useEffect(() => {
        fetchUnits();
    }, [fetchUnits]);

    useEffect(() => {
        fetchAllPods();
    }, []);

    const isFiltersApplied = hasActiveFilters(filters);

    const applyFilters = (nextFilters) => {
        updateQuery({ filters: nextFilters, page: FIRST_PAGE });
    };

    const resetFilters = () => {
        updateQuery({ filters: emptyFilters(ALL_UNIT_FILTER_SECTIONS), page: FIRST_PAGE });
    };

    const handleSortApply = ({ order, sortBy: nextSortBy }) => {
        updateQuery({ sortBy: nextSortBy, sortDirection: order });
    };

    const handleSortReset = () => {
        updateQuery({ sortBy: "", sortDirection: "" });
    };

    const handleCancel = () => {
        setShowDeleteModel(false);
        setSelectedUnitInfo(null);
    };

    const handleDeleteClick = (e, info) => {
        e.preventDefault();
        setSelectedUnitInfo(info);
        setShowDeleteModel(true);
    };


    const makeHandleRemoveConfirmCallback = (removeUnitsFromSelectedUnitsForBulkAction, notifyUnitRemovedFromZone) => async () => {
        const previousZone = getUnitZone(selectedUnitInfo);
        const wasClearingPendingZone = clearsPendingZone(selectedUnitInfo);
        try {
            setShowDeleteModel(false);
            await removeUnitFromZone(selectedUnitInfo);
            await fetchUnits();
            showToast(
                wasClearingPendingZone
                    ? PENDING_ZONE_REMOVED_MESSAGE
                    : "Unit removed from zone successfully"
            );
            if (filters.belongsToZone === "true") {
                removeUnitsFromSelectedUnitsForBulkAction(selectedUnitInfo);
            } else {
                notifyUnitRemovedFromZone(selectedUnitInfo.serialNumber, previousZone?.unid);
            }
        } catch (error) {
            showToast("Failed to remove unit", "failed");
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1025) {
                setGridView(true);  // Force gridView to true on small screens
                return;
            }
            setGridView(getStoredGridView());
        };

        handleResize(); // check on mount

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (window.innerWidth < 1025) return;
        try {
            localStorage.setItem(GRID_VIEW_STORAGE_KEY, gridView ? "grid" : "list");
        } catch (error) {
            // Ignore localStorage write failures (e.g. privacy mode).
        }
    }, [gridView]);

    const handleNavigate = () => {
        navigate('/dashboard')
    }

    const handleAddOrChangeZone = (e, unit) => {
        e.preventDefault();
        setSelectedUnitInfo(unit);  // store the clicked unit
        setShowEditModal(true);
    };

    const handleCloseZoneModal = () => {
        setShowEditModal(false);
    };

    const isZoneMembershipFiltered = filters.belongsToZone !== "";

    const applyZoneChange = useCallback(async (updateShownUnits) => {
        if (isZoneMembershipFiltered) {
            await fetchUnits();
            return;
        }
        setUnassociatedUnits(updateShownUnits);
    }, [fetchUnits, isZoneMembershipFiltered]);

    const makeHandleAddOrChangeZoneCallback = (notifyUnitChangedZone) => async (selectedZone) => {
        setShowEditModal(false);
        const previousZone = getUnitZone(selectedUnitInfo);

        try {
            const res = await assignUnitToZone(selectedUnitInfo, selectedZone);
            if (res.status === 200) {
                showToast(
                    isAwaitingConnection(selectedUnitInfo)
                        ? PENDING_ZONE_ASSIGNED_MESSAGE
                        : "Unit Updated successfully"
                );
                await applyZoneChange((prev) => prev.map((unit) => {
                    if (unit.serialNumber === selectedUnitInfo.serialNumber) {
                        return withZoneAssigned(unit, selectedZone);
                    } else {
                        return unit
                    }
                }));
                notifyUnitChangedZone(
                  selectedUnitInfo.serialNumber,
                  previousZone,
                  selectedZone,
              );
            }
        } catch (error) {
            if (error.message === "Failed to fetch") {
                showToast("Network error. Please try again.", 'failed')
            } else {
                console.error("Could not update the unit's zone", error);
                showToast("Could not update the unit's zone. Please try again.", 'failed')
            }
        }
    };

    const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

    const handlePageClick = ({ selected }) => {
        updateQuery({ page: selected + FIRST_PAGE });
    };

    return (
        <BulkUnitSelectionContainer
          onBulkChangeZone={(selectedUnitsForBulkAction, selectedFleetData) => applyZoneChange((prev) => prev.map((unit) => {
              if (selectedUnitsForBulkAction[unit.serialNumber]) {
                  return withZoneAssigned(unit, selectedFleetData);
              } else {
                  return unit
              }
          }))}
          onBulkRemoveFromZone={(selectedUnitsForBulkAction) => applyZoneChange((prev) => prev.map((unit) => {
              if (selectedUnitsForBulkAction[unit.serialNumber]) {
                  return withZoneCleared(unit);
              } else {
                  return unit
              }
          }))}
          setLoading={setLoading}
          showToast={showToast}
        >
            {
                ({
                    bulkActionHeader,
                    selectedUnitsForBulkAction,
                    addUnitsToSelectedUnitsForBulkAction,
                    removeUnitsFromSelectedUnitsForBulkAction,
                    notifyUnitRemovedFromZone,
                    notifyUnitChangedZone,
                }) => <>
                  <div className="titleAndFilters" data-testid="unassociated-units-title-and-filters">
                    <div className="flex flex-col gap-3 sm:flex-row justify-between items-start sm:items-center w-full">
                      <div className="flex gap-4 items-center justify-between w-full sm:w-auto sm:justify-normal">
                        <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="unassociated-units-title">
                          Manage Units {totalCount > 0 && (
                          <span>({totalCount})</span>)}
                        </h2>
                        <span className="text-xl leading-5 lg:text-2xl font-normal text-[#AFC3BC] dark:text-white lg:leading-6 tracking-[0.036px] hidden sm:block">|</span>
                        <h2 className="mainTitle text-[#FF8C65] font-semibold text-md leading-5 lg:text-base lg:leading-6 tracking-[0.036px] cursor-pointer underline" onClick={handleNavigate} data-testid="unassociated-units-goto-dashboard-link">Go to Dashboard</h2>
                      </div>
                      <div className="flex items-center gap-4 w-full sm:w-auto justify-between">
                        <SearchComponent
                          searchUnitText={searchUnitText}
                          setSearchUnitText={(val) => updateQuery({ search: val, page: FIRST_PAGE })}
                          trackingContext={SearchContext.manageUnits}
                        />
                        <SortByDropdown
                          data-testid="unassociated-units-sort-by-dropdown"
                          orderOptions={ORDER_OPTIONS}
                          sortByOptions={ALL_UNIT_SORT_OPTIONS}
                          selectedOrder={sortDirection}
                          selectedSortBy={sortBy}
                          onReset={handleSortReset}
                          onApply={handleSortApply}
                        />

                        <FilterDropDown
                          data-testid="unassociated-units-filter-dropdown"
                          sections={ALL_UNIT_FILTER_SECTIONS}
                          values={filters}
                          onApply={applyFilters}
                          onClearAll={resetFilters}
                        />

                        <div className="hidden lg:block">
                          <GridListViewComponent data-testid="unassociated-units-grid-list-view-toggle" gridView={gridView} setGridView={setGridView} />
                        </div>
                      </div>
                    </div>
                    {!gridView && bulkActionHeader}
                  </div>

                  {/* Grid and List view units data */}
                  {unassociatedUnits?.length > 0 && (
                    <>
                      {gridView ? (
                        <div className="mt-4 unitsHolder">
                          <div className="grid max-[430px]:grid-cols-1 grid-cols-[repeat(auto-fill,minmax(312px,1fr))] gap-6">
                            {unassociatedUnits.map((unit, index) => (
                              <UnassociatedUnitsCard
                                key={index}
                                info={unit}
                                handleDeleteClick={handleDeleteClick}
                                handleAddOrChangeZone={(e) => handleAddOrChangeZone(e, unit)}
                                selectedUnitsForBulkAction={selectedUnitsForBulkAction}
                                addUnitsToSelectedUnitsForBulkAction={addUnitsToSelectedUnitsForBulkAction}
                                removeUnitsFromSelectedUnitsForBulkAction={removeUnitsFromSelectedUnitsForBulkAction}
                                isActionsDisabled={isActionsDisabled}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 unitsHolder">
                          <UnassociatedUnitsList
                            unit={unassociatedUnits}
                            handleDeleteClick={handleDeleteClick}
                            handleAddOrChangeZone={handleAddOrChangeZone}
                            selectedUnitsForBulkAction={selectedUnitsForBulkAction}
                            addUnitsToSelectedUnitsForBulkAction={addUnitsToSelectedUnitsForBulkAction}
                            removeUnitsFromSelectedUnitsForBulkAction={removeUnitsFromSelectedUnitsForBulkAction}
                            isActionsDisabled={isActionsDisabled}
                          />
                        </div>
                      )}
                      {totalCount > DEFAULT_PAGE_SIZE && (
                        <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage}/>
                      )}
                    </>
                  )}

                  {/* Loading and when no zones data available */}
                  {loading ?
                    <LoadingModal />
                    : unassociatedUnits?.length === 0 && <div>
                    <p className="text-center dark:text-white text-dark-gray mt-4" data-testid="unassociated-units-no-results">
                      {isFiltersApplied
                        ? "No units found with the applied filters."
                        : searchUnitText
                          ? "No units match your search"
                          : "No units available"}
                    </p>
                  </div>
                  }

                  {showDeleteModel && (
                    <ConfirmModal
                      title="Remove Unit from Zone"
                      subText={
                        clearsPendingZone(selectedUnitInfo)
                          ? "Your unit will no longer be added to this zone when it connects."
                          : "Your unit will be unassigned."
                      }
                      confirmMessage="Are you sure you want to remove this unit from zone?"
                      cancelText="Cancel"
                      submitText="Confirm"
                      onCancel={handleCancel}
                      onSubmit={makeHandleRemoveConfirmCallback(removeUnitsFromSelectedUnitsForBulkAction, notifyUnitRemovedFromZone)}
                    />
                  )}

                  <ToastMessage
                    message={toast.message}
                    type={toast.type}
                    show={toast.show}
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                  />
                  {showZoneModal && selectedUnitInfo && (
                    <ZoneModal
                      title={getUnitZone(selectedUnitInfo) ? "Change Zone" : "Add to Zone"}
                      subText={
                        isAwaitingConnection(selectedUnitInfo)
                          ? "Your unit will be added to the selected zone when it connects to the cloud."
                          : getUnitZone(selectedUnitInfo)
                            ? "Your unit will be changed to the selected zone."
                            : "Your unit will be added to the selected zone."
                      }
                      confirmMessage={getUnitZone(selectedUnitInfo) ? "Are you sure you want to change to this zone?" : "Are you sure you want to add to this zone?"}
                      cancelText="Cancel"
                      submitText={getUnitZone(selectedUnitInfo) ? "Change" : "Add"}
                      onCancel={handleCloseZoneModal}
                      onSubmit={makeHandleAddOrChangeZoneCallback(notifyUnitChangedZone)}
                      shouldSetGloballySelectedFleet={false}
                    />
                  )}
                </>
            }
        </BulkUnitSelectionContainer>
    );
}
